import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  isFeatureFlagAllowedForViewer,
  type FeatureFlagMode,
  type FeatureFlagState,
} from '../flags';
import { featureFlagsApiRef } from '../api';

const DEFAULT_ADMIN_GROUP = 'group:default/backstage-admins';

interface FeatureFlagsContextValue {
  flags: FeatureFlagState[];
  loading: boolean;
  error: Error | null;
  /** True when the user is in `app.admin.group` (for `admins_only` mode). */
  isAdmin: boolean;
  isEnabled: (key: string) => boolean;
  refresh: () => Promise<void>;
  setFlag: (key: string, mode: FeatureFlagMode) => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(
  undefined,
);

/**
 * App-wide cache of runtime feature flags. Mount once near the app root so
 * sidebar, gates, and Settings share one source of truth.
 *
 * Viewer admin status comes from `app.admin.group` membership (same as the
 * Golden Paths backend). Managing flags in Settings uses a separate permission.
 */
export function FeatureFlagsProvider(props: { children: ReactNode }) {
  const featureFlagsApi = useApi(featureFlagsApiRef);
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const adminGroup = (
    configApi.getOptionalString('app.admin.group') ?? DEFAULT_ADMIN_GROUP
  ).trim();

  const [flags, setFlags] = useState<FeatureFlagState[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminLoading, setIsAdminLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    void (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        if (!isCancelled) {
          setIsAdmin(
            (identity.ownershipEntityRefs ?? []).includes(adminGroup),
          );
        }
      } catch {
        if (!isCancelled) {
          setIsAdmin(false);
        }
      } finally {
        if (!isCancelled) {
          setIsAdminLoading(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [identityApi, adminGroup]);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const next = await featureFlagsApi.listFlags();
      setFlags(next);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [featureFlagsApi]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Mid-session kill-switch: refresh when the window regains focus.
  useEffect(() => {
    const onFocus = () => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const setFlag = useCallback(
    async (key: string, mode: FeatureFlagMode) => {
      const updated = await featureFlagsApi.setFlag(key, mode);
      setFlags(current =>
        current.map(flag => (flag.key === key ? updated : flag)),
      );
    },
    [featureFlagsApi],
  );

  const isEnabled = useCallback(
    (key: string) => {
      const flag = flags.find(item => item.key === key);
      if (!flag) {
        return false;
      }
      return isFeatureFlagAllowedForViewer(flag.mode, isAdmin);
    },
    [flags, isAdmin],
  );

  const value = useMemo(
    () => ({
      flags,
      loading: isLoading || isAdminLoading,
      error,
      isAdmin,
      isEnabled,
      refresh,
      setFlag,
    }),
    [
      flags,
      isLoading,
      isAdminLoading,
      error,
      isAdmin,
      isEnabled,
      refresh,
      setFlag,
    ],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {props.children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags(): FeatureFlagsContextValue {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagsProvider');
  }
  return context;
}

/**
 * Convenience hook for a single flag. While flags / admin status are loading,
 * returns false so gated UI stays hidden by default.
 */
export function useFeatureFlag(key: string): {
  enabled: boolean;
  loading: boolean;
} {
  const { isEnabled, loading } = useFeatureFlags();
  return { enabled: !loading && isEnabled(key), loading };
}
